import { AuthFetch, Cancellation } from "@p0security/frontend-core";
import { AssessmentMap } from "@p0security/shared/assessment/dsl";
import { AggregatedGraph, aggregate } from "@p0security/shared/graph/aggregate";
import {
  Representation,
  fromRepresentation,
} from "@p0security/shared/graph/marshall";
import { addDigrams, addTypeBoost } from "@p0security/shared/graph/search";
import { ConnectedNode, DirectedGraph } from "@p0security/shared/graph/types";
import { ALL_SCOPE_SENTINEL } from "@p0security/shared/types/assessment";
import {
  AnyAggregates,
  AssessmentNodes,
  TargetNodeType,
} from "@p0security/shared/types/assessment/data/index";
import { YieldConfig, sleep } from "@p0security/util/sleep";
import { sortBy } from "lodash";

import { GraphProcessingStep } from "../components/GraphStep";
import { nodeDataFromShow } from "../components/node";

export type CachedMetagraph = {
  assessmentId: string;
  jobId: string;
  graph: DirectedGraph<AssessmentNodes>;
};

const N_BOOST_PHASES = 2;

export const transformGraphForUi = async (
  graph: Representation<AssessmentNodes>,
  setStep: (step: GraphProcessingStep) => void,
  setPercentage: (percentage: number) => void,
  options?: YieldConfig<ConnectedNode<AssessmentNodes, keyof AssessmentNodes>>
) => {
  /* eslint-disable no-console */
  setStep("converting");
  // b.c. the graph processing operations are synchronous, we need to give React
  // an opportunity to render prior to continuing
  await sleep(1);

  const output = {
    nodes: sortBy(fromRepresentation(graph).nodes, (n) => n.key),
  };
  const onSleep = (phaseIndex: number) => (_: any, nodeIndex: number) => {
    const percentage =
      (phaseIndex + nodeIndex / output.nodes.length) / N_BOOST_PHASES;
    setPercentage(percentage);
  };

  const beforeBoost = performance.now();
  setStep("boosting");
  await sleep(1);
  await addTypeBoost(output, { ...options, onSleep: onSleep(0) });
  const afterType = performance.now();
  console.log(
    "Time to add type boost",
    (afterType - beforeBoost).toFixed(1),
    "ms"
  );
  await addDigrams(output, AssessmentMap, {
    ...options,
    onSleep: onSleep(1),
  });
  console.log(
    "Time to add digram boost",
    (performance.now() - afterType).toFixed(1),
    "ms"
  );
  console.log(
    "Time to add search boost",
    (performance.now() - beforeBoost).toFixed(1),
    "ms"
  );
  setPercentage(0);

  /* eslint-enable no-console */
  return output;
};

export const fetchGraphForScope = async (params: {
  assessmentId: string;
  authFetch: AuthFetch;
  cancellation: Cancellation;
  effectiveScopeKey: string;
  lastJobId: string;
  setGraph: React.Dispatch<
    React.SetStateAction<DirectedGraph<AssessmentNodes> | undefined>
  >;
  setMetaGraph: React.Dispatch<
    React.SetStateAction<CachedMetagraph | undefined>
  >;
  setPercentage: React.Dispatch<React.SetStateAction<number>>;
  setStep: React.Dispatch<React.SetStateAction<GraphProcessingStep>>;
}) => {
  const {
    assessmentId,
    authFetch,
    cancellation,
    effectiveScopeKey,
    lastJobId,
    setGraph,
    setMetaGraph,
    setPercentage,
    setStep,
  } = params;

  setStep("requested");
  const before = performance.now();
  const response = await authFetch(
    `assessment/${assessmentId}/job/${lastJobId}/scope/${encodeURIComponent(
      effectiveScopeKey
    )}`,
    { method: "GET" }
  );

  // eslint-disable-next-line no-console
  console.log(
    "Initiated graph stream",
    (performance.now() - before).toFixed(1),
    "ms"
  );

  if (!response || cancellation?.isCancelled) return;

  setStep("loading");
  const data = (await response.json()) as Representation<AssessmentNodes>;
  // eslint-disable-next-line no-console
  console.log(
    "Received graph stream",
    (performance.now() - before).toFixed(1),
    "ms"
  );
  if (cancellation.isCancelled) {
    // eslint-disable-next-line no-console
    console.log("Graph stream cancelled");
    return;
  }

  const beforeTransform = performance.now();
  const newGraph = data
    ? await transformGraphForUi(
        data,
        cancellation.guard(setStep),
        cancellation.guard(setPercentage),
        { cancellation, maxOccupancyMs: 100 }
      )
    : undefined;

  // eslint-disable-next-line no-console
  console.log(
    "Time to transform graph",
    (performance.now() - beforeTransform).toFixed(1),
    "ms"
  );

  // Cache the metagraph for future recall
  if (effectiveScopeKey === ALL_SCOPE_SENTINEL && newGraph) {
    setMetaGraph({ assessmentId, jobId: lastJobId, graph: newGraph });
  }

  setGraph(newGraph);
  setStep("aggregating");
  return newGraph;
};

export const aggregateGraphForShow = async (params: {
  cancellation: Cancellation;
  graph: DirectedGraph<AssessmentNodes>;
  setAggregated: React.Dispatch<
    React.SetStateAction<
      AggregatedGraph<AssessmentNodes, AnyAggregates> | undefined
    >
  >;
  show: TargetNodeType;
}) => {
  const { cancellation, graph, setAggregated, show } = params;
  const { predicate, reducers } = nodeDataFromShow[show];

  const beforeAgg = performance.now();
  const subgraph = { nodes: graph.nodes.filter(predicate) ?? [] };
  const result = await aggregate<AssessmentNodes, AnyAggregates>(
    subgraph,
    reducers,
    { cancellation, maxOccupancyMs: 100 }
  );
  if (cancellation.isCancelled) return;
  // TODO: send to analytics
  // eslint-disable-next-line no-console
  console.log(
    "Time to aggregate graph",
    (performance.now() - beforeAgg).toFixed(1),
    "ms"
  );
  setAggregated(result);
  return result;
};
